import { StaffUsers } from "./StaffUsers";
import { StaffOrganization } from "./StaffOrganization";
import { useEffect, useState, type FormEvent } from "react";
type Mailbox = {
  address: string;
  name: string;
  active: boolean;
  quotaMiB: number;
  usedMiB: number;
  singleSignOn: boolean;
};
type Data = {
  domain: string;
  host: string;
  webmailUrl: string;
  items: Mailbox[];
  dns: { name: string; record: string; status: string }[] | null;
};
export function StaffEmail({
  api,
  mutate,
}: {
  api: <T>(path: string) => Promise<T>;
  mutate: <T>(path: string, body: unknown) => Promise<T>;
}) {
  const [data, setData] = useState<Data | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [attempt, setAttempt] = useState(0),
    [selected, setSelected] = useState<Mailbox | null>(null),
    [editing, setEditing] = useState(false),
    [notice, setNotice] = useState("");
  useEffect(() => {
    let active = true;
    void api<Data>("/admin/staff-email")
      .then((value) => {
        if (active) {
          setData(value);
          setError("");
        }
      })
      .catch((cause) => {
        if (active) setError((cause as Error).message);
      });
    return () => {
      active = false;
    };
  }, [api, attempt]);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const body: any = {
        name: form.get("name"),
        quotaMiB: Number(form.get("quotaMiB")),
      };
      if (selected) body.active = form.get("active") === "on";
      else body.localPart = form.get("localPart");
      const password = String(form.get("password") || "");
      if (password) body.password = password;
      await mutate(
        "/admin/staff-email" +
          (selected ? "/" + encodeURIComponent(selected.address) : ""),
        body,
      );
      setEditing(false);
      setSelected(null);
      setAttempt((x) => x + 1);
      setNotice(
        selected
          ? "Staff mailbox updated."
          : "Staff mailbox created. Give the staff member their temporary password securely and direct them to Staff email setup below to choose their own password.",
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const confirmAddress = String(
      new FormData(event.currentTarget).get("confirmAddress"),
    );
    setBusy(true);
    setError("");
    try {
      await mutate(
        "/admin/staff-email/" +
          encodeURIComponent(selected.address) +
          "/delete",
        { confirmAddress },
      );
      setEditing(false);
      setSelected(null);
      setAttempt((x) => x + 1);
      setNotice("Staff mailbox deleted.");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="cr-dashboard">
      <p>Company email, calendars, and contacts use your CRTC staff sign-in.</p>
      <StaffUsers api={api} mutate={mutate} />
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <div className="cr-dashboard-section-heading">
        <button
          className="cr-button secondary"
          disabled={busy}
          onClick={() => setAttempt((x) => x + 1)}
        >
          Refresh email status
        </button>
      </div>
      {data && (
        <>
          <section className="cr-panel">
            <h2>Email security</h2>
            {data.dns ? (
              <ul className="cr-service-status-list">
                {data.dns.map((check) => (
                  <li key={check.name}>
                    <div>
                      <strong>{check.name}</strong>
                      <p className="cr-form-note">{check.record}</p>
                    </div>
                    <span
                      className={
                        check.status === "healthy" ? "" : "cr-service-warning"
                      }
                    >
                      {check.status === "healthy"
                        ? "Verified"
                        : check.status === "needs_attention"
                          ? "Needs attention"
                          : "Could not verify"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>DNS checks are unavailable. Refresh to try again.</p>
            )}
            <p className="cr-form-note">
              These checks verify published mail records. They do not confirm
              delivery to a recipient’s inbox.
            </p>
          </section>
          <section className="cr-panel">
            <div className="cr-dashboard-section-heading">
              <h2>Staff mailboxes</h2>
              <a
                className="cr-button secondary"
                href="https://mail.cacherivertelephone.com/?iam_sso=1"
                target="_blank"
                rel="noreferrer"
              >
                Open webmail
              </a>
            </div>
            <p>
              Open webmail with your CRTC login. For a desktop or mobile mail
              app, create an app password in{" "}
              <a
                href="https://mail.cacherivertelephone.com/user"
                target="_blank"
                rel="noreferrer"
              >
                email settings
              </a>
              .
            </p>
            {data.items.length ? (
              <ul className="cr-service-status-list">
                {data.items.map((mailbox) => (
                  <li key={mailbox.address}>
                    <div>
                      <strong>{mailbox.name}</strong>
                      <p>{mailbox.address}</p>
                      <p className="cr-form-note">
                        {mailbox.active ? "Active" : "Disabled"} ·{" "}
                        {mailbox.usedMiB} MB used of {mailbox.quotaMiB} MB
                      </p>
                    </div>
                    <button
                      className="cr-text-button"
                      disabled={busy}
                      onClick={() => {
                        setSelected(mailbox);
                        setEditing(true);
                        setNotice("");
                      }}
                    >
                      Manage
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No staff mailboxes have been created yet.</p>
            )}
          </section>
          {editing && (
            <section className="cr-panel">
              <h2>
                {selected ? "Manage " + selected.address : "Add staff mailbox"}
              </h2>
              <form
                className="cr-form"
                key={selected?.address ?? "new"}
                onSubmit={save}
              >
                {!selected && (
                  <label>
                    Email address
                    <input
                      name="localPart"
                      required
                      maxLength={64}
                      autoCapitalize="none"
                      autoComplete="off"
                    />
                    @{data.domain}
                  </label>
                )}
                <label>
                  Staff member’s name
                  <input
                    name="name"
                    required
                    maxLength={120}
                    defaultValue={selected?.name ?? ""}
                  />
                </label>
                <label>
                  Mailbox storage in MB
                  <input
                    name="quotaMiB"
                    type="number"
                    min={256}
                    max={5120}
                    required
                    defaultValue={selected?.quotaMiB || 2048}
                  />
                </label>
                {!selected?.singleSignOn && (
                  <>
                    <label>
                      {selected
                        ? "New temporary password (leave blank to keep current password)"
                        : "Temporary password"}
                      <input
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        minLength={15}
                        maxLength={128}
                        required={!selected}
                      />
                    </label>
                    <p className="cr-form-note">
                      Use 15 or more characters with uppercase, lowercase, a
                      number, and a symbol. A new password must be changed at
                      first sign-in.
                    </p>
                  </>
                )}
                {selected && (
                  <label>
                    <input
                      name="active"
                      type="checkbox"
                      defaultChecked={selected.active}
                    />
                    Mailbox enabled
                  </label>
                )}
                <button className="cr-button" disabled={busy}>
                  {busy ? "Saving…" : "Save mailbox"}
                </button>
                <button
                  className="cr-button secondary"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setEditing(false);
                    setSelected(null);
                  }}
                >
                  Cancel
                </button>
              </form>
              {selected && (
                <details>
                  <summary>Delete mailbox permanently</summary>
                  <p>
                    This deletes the mailbox and its stored email. Type{" "}
                    {selected.address} to confirm.
                  </p>
                  <form className="cr-form" onSubmit={remove}>
                    <input
                      name="confirmAddress"
                      aria-label="Email address to delete"
                      type="email"
                      required
                    />
                    <button className="cr-button secondary" disabled={busy}>
                      Delete mailbox and email
                    </button>
                  </form>
                </details>
              )}
            </section>
          )}
          <StaffOrganization api={api} mutate={mutate} mailboxes={data.items} />
          <section className="cr-panel">
            <h2>Mail app settings</h2>
            <p>Username: your full staff email address.</p>
            <dl className="cr-details">
              <dt>Incoming mail</dt>
              <dd>{data.host} · IMAP 993 · SSL/TLS</dd>
              <dt>Outgoing mail</dt>
              <dd>
                {data.host} · SMTP 587 · STARTTLS · Authentication required
              </dd>
            </dl>
          </section>
        </>
      )}
    </div>
  );
}
